// src/components/eventos/components/EventosList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';

const formatarData = (dataString) => {
    if (!dataString) return 'Data não informada';
    try {
        const [ano, mes, dia] = dataString.split('T')[0].split('-');
        return `${dia}/${mes}/${ano}`;
    } catch { return 'Data inválida'; }
};

/* ---- SVG Icons ---- */
const CalendarIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
);
const PlayIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
);
const AlertCircleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);
const ShareIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
);

const STATUS = {
    em_andamento: { label: 'EM ANDAMENTO', color: '#2CCB90', bg: 'rgba(44,203,144,0.12)', border: 'rgba(44,203,144,0.25)' },
    concluido:    { label: 'CONCLUÍDO',    color: '#64748b', bg: 'rgba(100,116,139,0.1)',  border: 'rgba(100,116,139,0.2)' },
    agendado:     { label: 'AGENDADO',     color: '#2563eb', bg: 'rgba(37,99,235,0.08)',  border: 'rgba(37,99,235,0.2)' },
};

const EventosList = ({ supabase, session, onEditarEvento, onNovoEvento }) => {
    const [eventos, setEventos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [eventoAtivo, setEventoAtivo] = useState(null);
    const [tabAtiva, setTabAtiva] = useState(0);
    const toast = useToast();

    const fetchEventos = useCallback(async () => {
        setIsLoading(true);
        try {
            const clienteId = session.user.user_metadata?.cliente_id;
            let query = supabase.from('eventos').select('*').order('data', { ascending: true });
            if (clienteId) query = query.eq('cliente_id', clienteId);
            const { data, error } = await query;
            if (error) throw error;
            setEventos(data || []);
            setEventoAtivo(data?.find(e => e.status === 'em_andamento') || null);
        } catch (error) {
            console.error('Erro ao buscar eventos:', error);
            toast({ title: 'Erro ao carregar eventos', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsLoading(false);
        }
    }, [supabase, session.user.user_metadata, toast]);

    useEffect(() => { fetchEventos(); }, [fetchEventos]);

    const compartilharEvento = (id) => {
        const url = `${window.location.origin}/evento/${id}`;
        navigator.clipboard.writeText(url).then(() => {
            toast({ title: 'Link copiado!', description: 'Compartilhe o link para que qualquer pessoa veja a programação.', status: 'success', duration: 3000, isClosable: true });
        }).catch(() => {
            toast({ title: 'Link gerado', description: url, status: 'info', duration: 6000, isClosable: true });
        });
    };

    const podeIniciar = (evento) => {
        if (!evento) return false;
        const agora = new Date();
        const hoje = `${agora.getFullYear()}-${String(agora.getMonth()+1).padStart(2,'0')}-${String(agora.getDate()).padStart(2,'0')}`;
        const eventoData = evento.data?.split('T')[0] || evento.data;
        if (eventoData > hoje) return false;
        const primeiraHora = evento.tarefas?.[0]?.horaInicio;
        if (!primeiraHora) return true;
        const horaAtual = `${agora.getHours().toString().padStart(2,'0')}:${agora.getMinutes().toString().padStart(2,'0')}`;
        return horaAtual >= primeiraHora;
    };

    const excluirEvento = async (id, titulo) => {
        if (!window.confirm(`Tem certeza que deseja excluir o evento "${titulo}"?`)) return;
        try {
            const { error } = await supabase.from('eventos').delete().eq('id', id);
            if (error) throw error;
            toast({ title: 'Evento excluído', status: 'info', duration: 3000, isClosable: true });
            fetchEventos();
        } catch (error) {
            toast({ title: 'Erro ao excluir evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        }
    };

    const ativarEvento = async (evento) => {
        try {
            const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const tarefasAtualizadas = (evento.tarefas || []).map((t, idx) => ({
                ...t, status: idx === 0 ? 'em_andamento' : 'pendente',
                horaInicioReal: idx === 0 ? horaAtual : null, horaFimReal: null,
            }));
            const { error } = await supabase.from('eventos').update({
                status: 'em_andamento', hora_inicio_real: horaAtual, tarefas: tarefasAtualizadas,
            }).eq('id', evento.id);
            if (error) throw error;
            toast({ title: 'Evento iniciado', description: `${evento.titulo} está em andamento`, status: 'success', duration: 3000, isClosable: true });
            fetchEventos();
        } catch (error) {
            toast({ title: 'Erro ao iniciar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        }
    };

    const renderCard = (evento) => {
        const st = STATUS[evento.status] || STATUS.agendado;
        const emAndamento = evento.status === 'em_andamento';
        const concluido = evento.status === 'concluido';
        return (
            <div key={evento.id} className={`el-card ${emAndamento ? 'active' : ''}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{evento.titulo}</h3>
                            <span style={{ padding: '3px 10px', borderRadius: '100px', background: st.bg, border: `1px solid ${st.border}`, color: st.color, fontSize: '10px', fontWeight: 700 }}>
                                {st.label}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: evento.descricao ? '8px' : 0 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '8px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '12px' }}>
                                <CalendarIcon /> {formatarData(evento.data)}
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '8px', background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', color: '#2563eb', fontSize: '12px' }}>
                                <ClockIcon /> {evento.tarefas?.length || 0} tarefas
                            </span>
                        </div>
                        {evento.descricao && (
                            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {evento.descricao}
                            </p>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' }}>
                        {evento.status === 'agendado' && (
                            <button className="el-btn green" onClick={() => ativarEvento(evento)} disabled={!!eventoAtivo || !podeIniciar(evento)} title={!podeIniciar(evento) ? `Disponível em ${formatarData(evento.data)}${evento.tarefas?.[0]?.horaInicio ? ` às ${evento.tarefas[0].horaInicio}` : ''}` : ''}>
                                <PlayIcon /> Iniciar
                            </button>
                        )}
                        <button className="el-icon-btn green" onClick={() => compartilharEvento(evento.id)} title="Copiar link público">
                            <ShareIcon />
                        </button>
                        {!emAndamento && (
                            <>
                                <button className="el-icon-btn blue" onClick={() => onEditarEvento(evento)} disabled={concluido} title="Editar">
                                    <EditIcon />
                                </button>
                                <button className="el-icon-btn red" onClick={() => excluirEvento(evento.id, evento.titulo)} title="Excluir">
                                    <TrashIcon />
                                </button>
                            </>
                        )}
                        {emAndamento && (
                            <button className="el-icon-btn blue" onClick={() => onEditarEvento(evento)} title="Editar">
                                <EditIcon />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderEmpty = (tipo) => (
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#94a3b8' }}>
                <CalendarIcon />
            </div>
            <p style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 600, color: '#64748b' }}>
                {tipo === 'historico' ? 'Nenhum evento no histórico' : 'Nenhum evento agendado'}
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>
                {tipo === 'historico' ? 'Eventos concluídos aparecerão aqui' : 'Comece criando seu primeiro evento'}
            </p>
            {tipo !== 'historico' && (
                <button onClick={onNovoEvento} style={{ marginTop: '18px', padding: '9px 22px', borderRadius: '10px', background: 'linear-gradient(135deg, #2CCB90, #2DB8E1)', border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Criar Evento
                </button>
            )}
        </div>
    );

    const eventosAtuais = eventos.filter(e => e.status === 'agendado' || e.status === 'em_andamento');
    const eventosHistorico = eventos.filter(e => e.status === 'concluido');

    return (
        <>
            <style>{`
                @keyframes el-spin { to { transform: rotate(360deg); } }
                .el-card { background:#ffffff; border:1px solid #e2e8f0; border-radius:12px; padding:18px; transition:all 0.2s; box-shadow:0 1px 4px rgba(0,0,0,0.06); }
                .el-card:hover { background:#f8fafc; box-shadow:0 4px 12px rgba(0,0,0,0.08); }
                .el-card.active { background:rgba(44,203,144,0.04); border-color:rgba(44,203,144,0.25); }
                .el-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:8px; font-size:12px; font-weight:600; border:1px solid transparent; cursor:pointer; font-family:inherit; transition:all 0.15s; }
                .el-btn:disabled { opacity:0.4; cursor:not-allowed; }
                .el-btn.green { background:rgba(44,203,144,0.1); border-color:rgba(44,203,144,0.25); color:#2CCB90; }
                .el-btn.green:not(:disabled):hover { background:rgba(44,203,144,0.18); }
                .el-icon-btn { width:32px; height:32px; display:inline-flex; align-items:center; justify-content:center; border-radius:8px; border:1px solid transparent; cursor:pointer; transition:all 0.15s; }
                .el-icon-btn:disabled { opacity:0.3; cursor:not-allowed; }
                .el-icon-btn.blue { background:rgba(37,99,235,0.06); border-color:rgba(37,99,235,0.15); color:#2563eb; }
                .el-icon-btn.blue:not(:disabled):hover { background:rgba(37,99,235,0.12); }
                .el-icon-btn.red { background:rgba(239,68,68,0.06); border-color:rgba(239,68,68,0.15); color:#ef4444; }
                .el-icon-btn.red:not(:disabled):hover { background:rgba(239,68,68,0.12); }
                .el-icon-btn.green { background:rgba(44,203,144,0.08); border-color:rgba(44,203,144,0.2); color:#2CCB90; }
                .el-icon-btn.green:hover { background:rgba(44,203,144,0.15); }
                .el-tab { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; border:1px solid transparent; cursor:pointer; font-family:inherit; transition:all 0.2s; }
                .el-tab.active { background:rgba(44,203,144,0.1); border-color:rgba(44,203,144,0.25); color:#2CCB90; }
                .el-tab.inactive { background:#f8fafc; border-color:#e2e8f0; color:#64748b; }
                .el-tab.inactive:hover { background:#f0fdf8; color:#1e293b; }
            `}</style>

            <div style={{ padding: '24px', fontFamily: "'Inter', -apple-system, sans-serif" }}>
                {/* Banner evento ativo */}
                {eventoAtivo && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(44,203,144,0.08)', border: '1px solid rgba(44,203,144,0.2)', borderRadius: '10px', marginBottom: '20px', color: '#2CCB90' }}>
                        <AlertCircleIcon />
                        <span style={{ fontSize: '13px', fontWeight: 600 }}>
                            Evento em andamento: <span style={{ color: '#0f172a' }}>{eventoAtivo.titulo}</span>
                            {eventoAtivo.hora_inicio_real && <span style={{ color: '#94a3b8', fontWeight: 400 }}> — Iniciado às {eventoAtivo.hora_inicio_real}</span>}
                        </span>
                    </div>
                )}

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
                    <button className={`el-tab ${tabAtiva === 0 ? 'active' : 'inactive'}`} onClick={() => setTabAtiva(0)}>
                        <CalendarIcon /> Atuais e Próximos ({eventosAtuais.length})
                    </button>
                    <button className={`el-tab ${tabAtiva === 1 ? 'active' : 'inactive'}`} onClick={() => setTabAtiva(1)}>
                        Histórico ({eventosHistorico.length})
                    </button>
                </div>

                {/* Conteúdo */}
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid rgba(44,203,144,0.2)', borderTopColor: '#2CCB90', animation: 'el-spin 0.8s linear infinite' }} />
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {tabAtiva === 0
                            ? (eventosAtuais.length === 0 ? renderEmpty('atual') : eventosAtuais.map(renderCard))
                            : (eventosHistorico.length === 0 ? renderEmpty('historico') : eventosHistorico.map(renderCard))
                        }
                    </div>
                )}
            </div>
        </>
    );
};

export default EventosList;
